import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, CheckCircle2, History, ArrowRight, Undo2, Trash2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import {
  BrioModal, BrioModalBody, BrioModalDivider, BrioButton, BrioSelect,
} from '../../components/BrioComponents';
import VersionFields from './VersionFields';
import ItemActivityPanel from './ItemActivityPanel';
import { formatSeriesRange, isPurgeCode } from '../utils/itemCodeGenerator';
import { subtradesForTradeGroup } from '../utils/tradeGroupSubtrades';
import { EMPTY_DESCRIPTION_FORM, descriptionFormFields, validateDescriptionParts } from '../utils/descriptionParts';

// ============================================================================
// ItemEditorModal — edit everything about a library item
// ============================================================================
// The three classifications (trade group, chapter / sub-trade, trade type),
// each version's content, which version is active, and the item's recorded
// history.
//
// One active version per item: pressing Active on a version archives every
// other active version of the item (boq.set_active_version). A new version
// saved here starts archived, so the one in use stays in use until the curator
// presses Active on the new one; only an item with no active version gets its
// new version as active. Items that already carry several active versions
// keep them until someone presses Active.
//
// Versions moved in by a merge sit below a divider naming the code they had
// before (item_versions.merged_from_code).
//
// The classification is edited here and nowhere else: the library row shows it
// read only. Changing it regenerates the item code when the current code no
// longer fits (useBoqLibrary.proposeItemCode). A renumber is recorded and
// reaches live project BOQs and issued bill snapshots alike; library archives
// keep the old code. The modal says so, with the counts, before anything is
// saved. An item in Purge (OO.OO.nnnn) has no classification; picking one
// here is how it leaves Purge, and it always takes a new number.
//
// Editing a version respects the immutable-snapshot rule: a version that
// projects already reference is edited by creating a NEW version (with a
// change reason); a draft or unreferenced version is edited in place.
// ============================================================================

const statusBadge = (status) => {
  if (status === 'draft') return { label: 'candidate', bg: 'rgba(176,131,0,0.15)', fg: '#8a6d00' };
  if (status === 'active') return { label: 'active', bg: 'rgba(46,125,50,0.13)', fg: '#2e7d32' };
  if (status === 'deprecated') return { label: 'archived', bg: '#eee', fg: 'var(--brio-text-muted)' };
  return { label: status || '—', bg: '#eee', fg: 'var(--brio-text-muted)' };
};

const blankForm = {
  ...EMPTY_DESCRIPTION_FORM,
  shortDescription: '', unit: '', measurementRef: '',
  longSpecGeneral: '', longSpecProducts: '', longSpecExecution: '', changeReason: '',
};
const formFrom = (v) => ({
  ...descriptionFormFields(v),
  shortDescription: v.shortDescription || '',
  unit: v.unit || '',
  measurementRef: v.measurementRef || '',
  longSpecGeneral: v.longSpecGeneral || '',
  longSpecProducts: v.longSpecProducts || '',
  longSpecExecution: v.longSpecExecution || '',
  changeReason: '',
});

const classFrom = (library, itemCode) => ({
  tradeGroupNodeId: library.tradeGroupByItem.get(itemCode)?.id || '',
  brioSubtradeNodeId: library.brioSubtradeNodeByItem.get(itemCode) || '',
  tradeTypeNodeId: library.tradeTypeNodeByItem.get(itemCode) || '',
});

function ItemEditorModal({ library, itemCode, onClose, onReclassified }) {
  const {
    versions, tradeGroups, brioSubtrades, tradeTypes, loadGroupDetail,
    updateVersion, createVersion, setActiveVersion, unmergeItems,
    deleteVersion, canDeleteVersion,
    proposeItemCode, reclassifyItem, fetchItemActivity, subtradeLinks,
  } = library;

  const itemVersions = useMemo(
    () => versions.filter((v) => v.itemCode === itemCode).sort((a, b) => a.fullCode.localeCompare(b.fullCode)),
    [versions, itemCode]
  );
  // The item's own versions first, then one group per item merged into it,
  // each under a divider naming its old code. A merged_from_code equal to the
  // current code (an archive restore can put a version back) reads as own.
  const versionGroups = useMemo(() => {
    const own = []; const merged = new Map();
    itemVersions.forEach((v) => {
      const from = v.mergedFromCode && v.mergedFromCode !== itemCode ? v.mergedFromCode : null;
      if (!from) { own.push(v); return; }
      if (!merged.has(from)) merged.set(from, []);
      merged.get(from).push(v);
    });
    return [{ from: null, versions: own }, ...[...merged.entries()].map(([from, vs]) => ({ from, versions: vs }))];
  }, [itemVersions, itemCode]);

  const [usage, setUsage] = useState({});          // versionId → { projects, rates, bills }
  const [editingId, setEditingId] = useState(null); // version being edited in place / forked
  const [editForm, setEditForm] = useState(blankForm);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Make sure the heavy long-spec fields are loaded for this item.
  useEffect(() => { loadGroupDetail([itemCode]); }, [itemCode, loadGroupDetail]);

  // Per-version project + rate usage — drives the "in place vs new version"
  // decision and the badges.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = itemVersions.map((v) => v.id);
      if (!ids.length) return;
      const [piRes, rateRes, billRes] = await Promise.all([
        supabase.schema('boq').from('project_items').select('item_version_id').in('item_version_id', ids),
        supabase.schema('boq').from('rates').select('item_version_id').in('item_version_id', ids),
        // Issued bill snapshot lines: they follow a renumber too.
        supabase.schema('boq').from('bill_version_lines').select('item_version_id').in('item_version_id', ids),
      ]);
      if (cancelled) return;
      const tally = {};
      ids.forEach((id) => { tally[id] = { projects: 0, rates: 0, bills: 0 }; });
      (piRes.data || []).forEach((r) => { if (tally[r.item_version_id]) tally[r.item_version_id].projects += 1; });
      (rateRes.data || []).forEach((r) => { if (tally[r.item_version_id]) tally[r.item_version_id].rates += 1; });
      (billRes.data || []).forEach((r) => { if (tally[r.item_version_id]) tally[r.item_version_id].bills += 1; });
      setUsage(tally);
    })();
    return () => { cancelled = true; };
  }, [itemVersions]);

  // ── Classification ──────────────────────────────────────────────────────
  // A draft of the three pickers, compared against what is saved. Re-seeded
  // whenever the saved classification changes, which includes a renumber
  // handing this modal a new itemCode.
  const saved = classFrom(library, itemCode);
  const savedKey = `${saved.tradeGroupNodeId}|${saved.brioSubtradeNodeId}|${saved.tradeTypeNodeId}`;
  const [cls, setCls] = useState(saved);
  useEffect(() => {
    const [tradeGroupNodeId, brioSubtradeNodeId, tradeTypeNodeId] = savedKey.split('|');
    setCls({ tradeGroupNodeId, brioSubtradeNodeId, tradeTypeNodeId });
  }, [itemCode, savedKey]);
  const clsDirty = cls.tradeGroupNodeId !== saved.tradeGroupNodeId
    || cls.brioSubtradeNodeId !== saved.brioSubtradeNodeId
    || cls.tradeTypeNodeId !== saved.tradeTypeNodeId;

  // The sub-trades the chosen trade group offers (ticked on the Taxonomies
  // page, or all while it has none). The item's saved sub-trade always stays
  // listed, marked, so an existing classification is never hidden.
  const offered = useMemo(
    () => subtradesForTradeGroup(brioSubtrades || [], cls.tradeGroupNodeId, subtradeLinks, saved.brioSubtradeNodeId),
    [brioSubtrades, cls.tradeGroupNodeId, subtradeLinks, saved.brioSubtradeNodeId]
  );
  const subtradesByChapter = useMemo(() => {
    const order = []; const byChapter = new Map();
    offered.subtrades.forEach((sub) => {
      if (!byChapter.has(sub.chapterLabel)) { byChapter.set(sub.chapterLabel, []); order.push(sub.chapterLabel); }
      byChapter.get(sub.chapterLabel).push(sub);
    });
    return order.map((label) => ({ label, subs: byChapter.get(label) }));
  }, [offered]);

  // Moving to a trade group that does not offer the chosen sub-trade clears it,
  // unless it is the item's saved one, which stays selectable.
  useEffect(() => {
    if (!cls.brioSubtradeNodeId) return;
    if (offered.subtrades.some((sub) => String(sub.id) === String(cls.brioSubtradeNodeId))) return;
    setCls((c) => ({ ...c, brioSubtradeNodeId: '', tradeTypeNodeId: '' }));
  }, [offered, cls.brioSubtradeNodeId]);
  const typesForSubtrade = useMemo(
    () => (tradeTypes || []).filter((t) => t.anchorId && String(t.anchorId) === String(cls.brioSubtradeNodeId)),
    [tradeTypes, cls.brioSubtradeNodeId]
  );
  const chosenSubtrade = (brioSubtrades || []).find((sub) => sub.id === cls.brioSubtradeNodeId) || null;
  const noTypesHere = !!cls.brioSubtradeNodeId && typesForSubtrade.length === 0;

  // A trade type belongs to one sub-trade; moving sub-trade drops it.
  const pickSubtrade = (id) => setCls((c) => ({
    ...c,
    brioSubtradeNodeId: id,
    tradeTypeNodeId: (tradeTypes || []).some((t) => t.id === c.tradeTypeNodeId && String(t.anchorId) === String(id))
      ? c.tradeTypeNodeId : '',
  }));

  const proposal = clsDirty ? proposeItemCode(itemCode, cls) : null;

  const usedTotals = useMemo(() => Object.values(usage).reduce(
    (acc, u) => ({ projects: acc.projects + (u.projects || 0), bills: acc.bills + (u.bills || 0) }),
    { projects: 0, bills: 0 }
  ), [usage]);

  const saveClassification = async () => {
    setError(null);
    if (!proposal || proposal.error) { setError(proposal?.error || 'Nothing to save.'); return; }
    setBusy(true);
    const res = await reclassifyItem(itemCode, cls);
    setBusy(false);
    if (!res.success) { setError(res.error || 'Failed to save the classification.'); return; }
    // The page follows the item: new code, and the trade group it now sits in.
    if (onReclassified) {
      onReclassified({
        oldCode: itemCode,
        newCode: res.itemCode,
        tradeGroup: tradeGroups.find((n) => n.id === cls.tradeGroupNodeId) || null,
      });
    }
  };

  const [showHistory, setShowHistory] = useState(false);
  // A version referenced by projects must fork into a new version on edit.
  const mustFork = (v) => v.status !== 'draft' && (usage[v.id]?.projects || 0) > 0;

  const startEdit = (v) => { setError(null); setAdding(false); setEditingId(v.id); setEditForm(formFrom(v)); };
  const cancelEdit = () => { setEditingId(null); setEditForm(blankForm); };

  const saveEdit = async (v) => {
    setError(null);
    const partsError = validateDescriptionParts(editForm);
    if (partsError) { setError(partsError); return; }
    if (!editForm.unit.trim()) { setError('Unit is required.'); return; }
    const fork = mustFork(v);
    if (fork && !editForm.changeReason.trim()) {
      setError('This version is used by projects — a change reason is required to save it as a new version.');
      return;
    }
    setBusy(true);
    const res = fork
      ? await createVersion(itemCode, { ...editForm, status: newVersionStatus })
      : await updateVersion(v.id, editForm);
    setBusy(false);
    if (res.success) cancelEdit();
    else setError(res.error || 'Failed to save.');
  };

  const handleAdd = async () => {
    setError(null);
    const partsError = validateDescriptionParts(newForm);
    if (partsError) { setError(partsError); return; }
    if (!newForm.unit.trim()) { setError('Unit is required.'); return; }
    if (!newForm.changeReason.trim()) { setError('Change reason is required for a new version.'); return; }
    setBusy(true);
    const res = await createVersion(itemCode, { ...newForm, status: newVersionStatus });
    setBusy(false);
    if (res.success) {
      setAdding(false); setNewForm(blankForm);
      if (res.version?.id) setMadeHere((prev) => new Set(prev).add(res.version.id));
    }
    else setError(res.error || 'Failed to create version.');
  };

  const activeCount = itemVersions.filter((v) => v.status === 'active').length;
  // A new version waits archived while another is in use (see header).
  const newVersionStatus = activeCount > 0 ? 'deprecated' : 'active';

  // Versions made with + New version since this modal opened. Only these offer
  // Delete; closing the modal forgets them, so an older version never does.
  // boq.delete_item_version() still refuses an active, last or referenced one.
  const [madeHere, setMadeHere] = useState(() => new Set());
  const [deleteArmed, setDeleteArmed] = useState(null);     // version id awaiting confirm
  const handleDeleteVersion = async (v) => {
    if (deleteArmed !== v.id) { setDeleteArmed(v.id); return; }
    setError(null);
    setBusy(true);
    const res = await deleteVersion(v.id);
    setBusy(false);
    setDeleteArmed(null);
    if (res.success) setMadeHere((prev) => { const n = new Set(prev); n.delete(v.id); return n; });
    else setError(res.error || 'Failed to delete the version.');
  };

  // Unmerge: two clicks on the divider's button, the first arms it.
  const [unmergeArmed, setUnmergeArmed] = useState(null);   // from-code awaiting confirm
  const handleUnmerge = async (fromCode) => {
    if (unmergeArmed !== fromCode) { setUnmergeArmed(fromCode); return; }
    setError(null);
    setBusy(true);
    const res = await unmergeItems(itemCode, fromCode);
    setBusy(false);
    setUnmergeArmed(null);
    if (!res.success) setError(res.error || 'Failed to unmerge.');
  };

  const handleMakeActive = async (v) => {
    setError(null);
    setBusy(true);
    const res = await setActiveVersion(v.id);
    setBusy(false);
    if (!res.success) setError(res.error || 'Failed to change the active version.');
  };


  const startAdd = () => {
    setEditingId(null);
    const latest = itemVersions.filter((v) => v.status === 'active').slice(-1)[0] || itemVersions.slice(-1)[0];
    setNewForm(latest ? { ...formFrom(latest), changeReason: '' } : blankForm);
    setAdding(true);
  };

  const footer = (
    <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Done</BrioButton>
  );

  const labelStyle = { fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)' };

  return (
    <BrioModal title={<>Edit item <code>{itemCode}</code></>} onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {error && (
          <div style={{ padding: '0.6rem 0.9rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '0.9rem' }}>{error}</div>
        )}

        {/* Identity, status, and the history toggle at the right end */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <div>
            <div style={labelStyle}>Item code</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <code style={{ fontSize: '0.95rem', fontWeight: 600 }}>{itemCode}</code>
              {proposal?.renumber && (
                <>
                  <ArrowRight size={14} style={{ color: 'var(--brio-gold, #9F902A)' }} />
                  <code style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--brio-gold, #9F902A)' }}>{proposal.code}</code>
                </>
              )}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--brio-text-muted)' }}>
              {isPurgeCode(itemCode)
                ? 'In Purge. Pick a trade group, chapter / subtrade and type below to move it out.'
                : 'Generated from the classification below.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            title={showHistory ? 'Back to the item' : 'Item activity: every recorded change to this item'}
            aria-pressed={showHistory}
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: '0.78rem', padding: '0.4rem 0.6rem', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${showHistory ? 'var(--brio-gold, #9F902A)' : 'var(--brio-border, #ccc)'}`,
              background: showHistory ? 'rgba(159,144,42,0.12)' : 'transparent',
              color: showHistory ? 'var(--brio-gold, #9F902A)' : 'inherit',
            }}
          >
            <History size={15} /> Activity
          </button>
        </div>

        {showHistory ? (
          <>
            <BrioModalDivider title="Activity" />
            <ItemActivityPanel key={itemCode} itemCode={itemCode} fetchItemActivity={fetchItemActivity} />
          </>
        ) : (
        <>
        {/* Classification: the only place it can be changed */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <label style={{ display: 'block', minWidth: 0 }}>
            <div style={labelStyle}>Trade group</div>
            <BrioSelect value={cls.tradeGroupNodeId} onChange={(e) => setCls((c) => ({ ...c, tradeGroupNodeId: e.target.value }))} disabled={busy}>
              <option value="">— Select trade group —</option>
              {tradeGroups.map((n) => (
                <option key={n.id} value={n.id}>{n.shortCode ? `${n.shortCode} · ` : ''}{n.label}</option>
              ))}
            </BrioSelect>
          </label>
          <label style={{ display: 'block', minWidth: 0 }}>
            <div style={labelStyle}>Chapter / sub-trade</div>
            <BrioSelect value={cls.brioSubtradeNodeId} onChange={(e) => pickSubtrade(e.target.value)} disabled={busy}>
              <option value="">— Select chapter / sub-trade —</option>
              {subtradesByChapter.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.subs.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.shortCode ? `${sub.shortCode} · ` : ''}{sub.label}
                      {offered.outside === String(sub.id) ? ' (not ticked for this trade group)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </BrioSelect>
          </label>
          <label style={{ display: 'block', minWidth: 0 }}>
            <div style={labelStyle}>Trade type</div>
            <BrioSelect
              value={cls.tradeTypeNodeId}
              onChange={(e) => setCls((c) => ({ ...c, tradeTypeNodeId: e.target.value }))}
              disabled={busy || !cls.brioSubtradeNodeId || noTypesHere}
            >
              <option value="">
                {!cls.brioSubtradeNodeId
                  ? '— Pick a chapter / sub-trade first —'
                  : (noTypesHere
                    ? `— None; numbered from ${chosenSubtrade?.seriesStart != null ? formatSeriesRange(chosenSubtrade.seriesStart, chosenSubtrade.seriesEnd) : 'the sub-trade range'} —`
                    : '— Select trade type —')}
              </option>
              {typesForSubtrade.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}{t.seriesStart != null ? ` · ${formatSeriesRange(t.seriesStart, t.seriesEnd)}` : ' · no series set'}
                </option>
              ))}
            </BrioSelect>
          </label>
        </div>

        {clsDirty && (
          <div style={{ fontSize: '0.78rem', borderRadius: 4, padding: '0.5rem 0.7rem', marginBottom: '0.5rem', background: proposal?.error ? '#fdecec' : 'rgba(176,131,0,0.10)', color: proposal?.error ? '#a94442' : '#8a6d00' }}>
            {proposal?.error ? proposal.error : (proposal.renumber ? (
              <>
                Saving renumbers <code>{itemCode}</code> to <strong><code>{proposal.code}</code></strong>
                {cls.tradeGroupNodeId !== saved.tradeGroupNodeId && (
                  <> and moves it to <strong>{tradeGroups.find((n) => n.id === cls.tradeGroupNodeId)?.label}</strong></>
                )}.
                {(usedTotals.projects > 0 || usedTotals.bills > 0) ? (
                  <> It is used on {usedTotals.projects} project line{usedTotals.projects === 1 ? '' : 's'} and {usedTotals.bills} issued bill snapshot line{usedTotals.bills === 1 ? '' : 's'}. Project BOQs and issued estimates and tenders will all show the new code.</>
                ) : ' It is not used on any project or issued bill yet.'}
                {' '}The old code is recorded in the item&apos;s activity and becomes free for the next item in its series.
              </>
            ) : (
              <>The code <code>{itemCode}</code> already fits this classification, so it stays as it is.</>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <BrioButton variant="primary" onClick={saveClassification} disabled={busy || !!proposal?.error}>
                {busy ? 'Saving…' : (proposal?.renumber ? 'Save and renumber' : 'Save classification')}
              </BrioButton>
              <BrioButton variant="secondary" onClick={() => setCls(saved)} disabled={busy}>Reset</BrioButton>
            </div>
          </div>
        )}

        <BrioModalDivider title={`Versions (${itemVersions.length})`} />

        {activeCount > 1 && (
          <div style={{ fontSize: '0.76rem', color: '#8a6d00', background: 'rgba(176,131,0,0.10)', borderRadius: 4, padding: '0.4rem 0.6rem', marginBottom: '0.6rem' }}>
            {activeCount} versions are active. Press <strong>Active</strong> on the one to keep in use; the others are archived and no longer offered in project BOQs.
          </div>
        )}
        {versionGroups.map((g) => (g.versions.length === 0 ? null : (
        <React.Fragment key={g.from || '__own__'}>
        {g.from && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0.9rem 0 0.6rem' }}>
            <div style={{ flex: 1, borderTop: '1px solid var(--brio-border, #ccc)' }} />
            <span style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)', whiteSpace: 'nowrap' }}>
              Merged from <code style={{ fontWeight: 600 }}>{g.from}</code>
            </span>
            <div style={{ flex: 1, borderTop: '1px solid var(--brio-border, #ccc)' }} />
            <button type="button" onClick={() => handleUnmerge(g.from)} disabled={busy}
              onBlur={() => setUnmergeArmed((a) => (a === g.from ? null : a))}
              title={`Move these versions back to ${g.from} with their original letters, status and classification`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', borderRadius: 4, cursor: 'pointer',
                border: `1px solid ${unmergeArmed === g.from ? '#a94442' : 'var(--brio-border, #ccc)'}`,
                background: unmergeArmed === g.from ? '#fdecec' : 'transparent',
                color: unmergeArmed === g.from ? '#a94442' : 'inherit',
              }}>
              <Undo2 size={12} /> {unmergeArmed === g.from ? `Confirm: restore ${g.from}` : 'Unmerge'}
            </button>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {g.versions.map((v) => {
            const b = statusBadge(v.status);
            const u = usage[v.id] || { projects: 0, rates: 0 };
            const editing = editingId === v.id;
            const fork = mustFork(v);
            return (
              <div key={v.id} style={{ border: editing ? '2px solid var(--brio-gold, #9F902A)' : '1px solid var(--brio-border, #ccc)', borderRadius: 6, padding: '0.7rem', background: v.status === 'deprecated' ? '#fafafa' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: '0.82rem', fontWeight: 600 }}>{v.fullCode}</code>
                  <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', padding: '0 5px', borderRadius: 3, background: b.bg, color: b.fg }}>{b.label}</span>
                  <span style={{ fontSize: '0.72rem', color: u.projects > 0 ? '#8a6d00' : 'var(--brio-text-muted)' }}>
                    used by {u.projects} project{u.projects === 1 ? '' : 's'} · {u.rates} rate{u.rates === 1 ? '' : 's'}
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                    {!editing && (
                      <>
                        <button type="button" onClick={() => startEdit(v)} disabled={busy}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer' }}>
                          <Pencil size={12} /> Edit
                        </button>
                        {(() => {
                          // The one in use reads as a filled, pressed Active; any
                          // other version offers it. Only one stays active.
                          const isOnlyActive = v.status === 'active' && activeCount === 1;
                          return (
                            <button type="button" onClick={() => handleMakeActive(v)} disabled={busy || isOnlyActive}
                              aria-pressed={v.status === 'active'}
                              title={isOnlyActive ? 'This is the version in use'
                                : 'Make this the version in use; the item\'s other active versions are archived'}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', borderRadius: 4,
                                cursor: isOnlyActive ? 'default' : 'pointer',
                                border: `1px solid ${v.status === 'active' ? '#2e7d32' : 'var(--brio-border, #ccc)'}`,
                                background: isOnlyActive ? 'rgba(46,125,50,0.13)' : 'transparent',
                                color: v.status === 'active' ? '#2e7d32' : 'inherit',
                              }}>
                              <CheckCircle2 size={12} /> Active
                            </button>
                          );
                        })()}
                        {canDeleteVersion && madeHere.has(v.id) && (() => {
                          const u2 = usage[v.id] || {};
                          const blockedWhy = itemVersions.length < 2 ? 'This is the only version; delete the item instead'
                            : v.status === 'active' ? 'Make another version active before deleting this one'
                            : (u2.projects || u2.rates || u2.bills) ? 'Used by a project, a rate or an issued estimate, so it cannot be deleted'
                            : null;
                          const armed = deleteArmed === v.id;
                          return (
                            <button type="button" onClick={() => handleDeleteVersion(v)} disabled={busy || !!blockedWhy}
                              onBlur={() => setDeleteArmed((a) => (a === v.id ? null : a))}
                              title={blockedWhy || (armed ? 'Click again to delete this version' : 'Delete this new version')}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', borderRadius: 4,
                                cursor: blockedWhy ? 'not-allowed' : 'pointer', opacity: blockedWhy ? 0.45 : 1,
                                border: `1px solid ${armed ? '#a94442' : 'var(--brio-border, #ccc)'}`,
                                background: armed ? '#fdecec' : 'transparent',
                                color: blockedWhy ? 'inherit' : '#a94442',
                              }}>
                              <Trash2 size={12} /> {armed ? 'Confirm delete' : 'Delete'}
                            </button>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {!editing ? (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.82rem' }}>
                    {v.shortDescription} <span style={{ color: 'var(--brio-text-muted)' }}>· {v.unit}</span>
                    {v.changeReason && <div style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)', marginTop: 2 }}>↳ {v.changeReason}</div>}
                  </div>
                ) : (
                  <div style={{ marginTop: '0.6rem' }}>
                    {fork && (
                      <div style={{ fontSize: '0.76rem', color: '#8a6d00', background: 'rgba(176,131,0,0.10)', borderRadius: 4, padding: '0.4rem 0.6rem', marginBottom: '0.6rem' }}>
                        This version is used by {u.projects} project{u.projects === 1 ? '' : 's'}, so saving creates a <strong>new version</strong> (the original is preserved). A change reason is required.
                      </div>
                    )}
                    <VersionFields value={editForm} onChange={(f, val) => setEditForm((s) => ({ ...s, [f]: val }))} includeChangeReason={fork} changeReasonRequired={fork} />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                      <BrioButton variant="primary" onClick={() => saveEdit(v)} disabled={busy}>
                        {busy ? 'Saving…' : (fork ? 'Save as new version' : 'Save changes')}
                      </BrioButton>
                      <BrioButton variant="secondary" onClick={cancelEdit} disabled={busy}>Cancel</BrioButton>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </React.Fragment>
        )))}

        {adding ? (
          <div style={{ marginTop: '0.75rem', border: '2px solid var(--brio-gold, #9F902A)', borderRadius: 6, padding: '0.7rem', background: '#fffdf5' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>New version</div>
            <VersionFields value={newForm} onChange={(f, val) => setNewForm((s) => ({ ...s, [f]: val }))} includeChangeReason changeReasonRequired />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
              <BrioButton variant="primary" onClick={handleAdd} disabled={busy}>{busy ? 'Creating…' : 'Create version'}</BrioButton>
              <BrioButton variant="secondary" onClick={() => setAdding(false)} disabled={busy}>Cancel</BrioButton>
            </div>
          </div>
        ) : (
          <button type="button" onClick={startAdd} disabled={busy}
            style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', padding: '0.35rem 0.7rem', background: 'transparent', border: '1px dashed var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer' }}>
            <Plus size={14} /> New version
          </button>
        )}
        </>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default ItemEditorModal;
